import { NameFormat, formatName } from 'src/utils/nameFormatting';
import { Income, IncomeType } from './hooks/useIncomes';
import { Expenditure } from './hooks/useExpenditures';
import { LifeEvent } from './hooks/useLifeEvents';
import { CashflowGroup, GroupMember, groupCashflowItems } from './grouping';
import { CashflowFrequency } from './utils';

/**
 * A shared item's figures are the sum of its members' rows, so a row reads as the single item
 * the adviser entered rather than one member's slice of it. Type, description and frequency are
 * taken from the first row: they were entered once and copied to each member, and only the
 * family tab — which holds every row of a group — can change them, so the copies cannot drift.
 * Amounts stay as recorded, at the item's frequency, and are converted where they are shown. An
 * amount no member has recorded stays null rather than reading as zero.
 */
export type IncomeRow = {
  id: string;
  members: GroupMember[];
  incomeType: IncomeType;
  description: string | null;
  frequency: CashflowFrequency | null;
  amountPreTax: number | null;
  amountPostTax: number | null;
  items: Income[];
};

export type ExpenditureRow = {
  id: string;
  members: GroupMember[];
  category: string | null;
  description: string | null;
  frequency: CashflowFrequency | null;
  amount: number | null;
  items: Expenditure[];
};

export type LifeEventRow = {
  id: string;
  members: GroupMember[];
  description: string;
  eventDate: string | null;
  details: string | null;
  cost: number | null;
  items: LifeEvent[];
};

export function buildIncomeRows(
  incomes: Income[],
  memberByClientId: Map<string, GroupMember>,
): IncomeRow[] {
  return groupCashflowItems(incomes, memberByClientId).map((group: CashflowGroup<Income>) => {
    const [first] = group.items;
    return {
      id: group.key,
      members: group.members,
      incomeType: first.incomeType,
      description: first.description,
      frequency: first.frequency,
      amountPreTax: sumRecorded(group.items, item => item.amountPreTax),
      amountPostTax: sumRecorded(group.items, item => item.amountPostTax),
      items: group.items,
    };
  });
}

export function buildExpenditureRows(
  expenditures: Expenditure[],
  memberByClientId: Map<string, GroupMember>,
): ExpenditureRow[] {
  return groupCashflowItems(expenditures, memberByClientId).map(group => {
    const [first] = group.items;
    return {
      id: group.key,
      members: group.members,
      category: first.category,
      description: first.description,
      frequency: first.frequency,
      amount: sumRecorded(group.items, item => item.amount),
      items: group.items,
    };
  });
}

export function buildLifeEventRows(
  lifeEvents: LifeEvent[],
  memberByClientId: Map<string, GroupMember>,
): LifeEventRow[] {
  return groupCashflowItems(lifeEvents, memberByClientId).map(group => {
    const [first] = group.items;
    return {
      id: group.key,
      members: group.members,
      description: String(first.description ?? ''),
      eventDate: first.eventDate,
      details: first.details,
      cost: sumRecorded(group.items, item => item.cost),
      items: group.items,
    };
  });
}

function sumRecorded<T>(items: T[], select: (item: T) => number | null): number | null {
  const recorded = items.map(select).filter((amount): amount is number => amount != null);
  return recorded.length === 0 ? null : recorded.reduce((total, amount) => total + amount, 0);
}

/**
 * Explains a shared item's figure when only some of its members have the amount recorded, so
 * the sum is not read as the whole item. Undefined when every member, or none, has it.
 */
export function partialAmountNote<T extends { clientId: string }>(
  row: { members: GroupMember[]; items: T[] },
  select: (item: T) => number | null,
): string | undefined {
  const missing = row.items.filter(item => select(item) == null);
  if (missing.length === 0 || missing.length === row.items.length) return undefined;

  const recorded = row.items.filter(item => select(item) != null);
  const namesOf = (items: T[]) =>
    items
      .map(item => row.members.find(member => member.clientId === item.clientId))
      .filter((member): member is GroupMember => member != null)
      .map(member => formatName(member.firstName, member.lastName, NameFormat.NATURAL))
      .join(', ');

  return `Not recorded for ${namesOf(missing)}. This figure includes ${namesOf(recorded)} only.`;
}
